import { useQuery } from '@tanstack/react-query';
import { getDiskFolderChildren, fetchDiskFileById } from '../disk';
import { parseNcReference, lookupNc } from './parseNcReference';
import type { NcReferenceMap } from './types';
// TODO: Убрать отладочные логи
import { dbg } from './debugLog';

const REFERENCE_FOLDER_ID = import.meta.env.VITE_BX_REFERENCE_FOLDER_ID;

/**
 * Keywords for matching reference file names.
 * Files whose name contains any of these are prioritized.
 */
const FILENAME_KEYWORDS = ['справочник', 'нч', 'нормочас'];

/**
 * Check if a filename looks like a reference file by keywords.
 */
function isReferenceFileName(name: string): boolean {
  const lower = name.toLowerCase();
  return FILENAME_KEYWORDS.some((kw) => lower.includes(kw));
}

/**
 * Hook that loads the Нч reference data from Bitrix24 Disk.
 *
 * Search strategy:
 *  1. List all files in the reference folder (VITE_BX_REFERENCE_FOLDER_ID)
 *  2. Find xlsx files whose name contains reference keywords (справочник, нч, нормочас)
 *  3. If found — download and parse (last/newest first if multiple)
 *  4. If no name match or parse fails — download each remaining xlsx and try structural detection
 *
 * Returns NcReferenceMap (operation → Нч) or null if folder ID not configured.
 */
export function useNcReference() {
  return useQuery<NcReferenceMap | null, Error>({
    queryKey: ['ncReference'],
    queryFn: async () => {
      // TODO: Убрать отладочные логи — начало
      if (!REFERENCE_FOLDER_ID) {
        dbg('VITE_BX_REFERENCE_FOLDER_ID не задан — справочник отключен');
        return null;
      }

      const folderId = parseInt(REFERENCE_FOLDER_ID, 10);
      if (!Number.isFinite(folderId) || folderId <= 0) {
        dbg(`VITE_BX_REFERENCE_FOLDER_ID="${REFERENCE_FOLDER_ID}" — не число, справочник отключен`);
        return null;
      }
      dbg(`Начинаю загрузку справочника. folderId=${folderId}`);
      // TODO: Убрать отладочные логи — конец

      // 1. List files in the reference folder
      // TODO: Убрать отладочные логи — начало
      let children;
      try {
        children = await getDiskFolderChildren(folderId);
        dbg(`Получен список файлов папки: ${children.length} элементов`);
        children.forEach((c) => {
          dbg(`  — ${c.NAME} ${c.DOWNLOAD_URL ? '(файл)' : '(папка)'}`);
        });
      } catch (e) {
        dbg(`ОШИБКА получения списка файлов: ${e instanceof Error ? e.message : String(e)}`);
        throw e;
      }
      // TODO: Убрать отладочные логи — конец

      const xlsxFiles = children.filter(
        (c) => c.DOWNLOAD_URL && /\.(xlsx|xls)$/i.test(c.NAME),
      );

      // TODO: Убрать отладочные логи — начало
      dbg(`Найдено xlsx-файлов: ${xlsxFiles.length}`);
      if (xlsxFiles.length === 0) {
        dbg('В папке нет xlsx-файлов — справочник не найден');
        throw new Error('В папке справочников нет xlsx-файлов');
      }
      // TODO: Убрать отладочные логи — конец

      // 2. Try files matching by name first (last = newest in Bitrix API order)
      const nameMatches = xlsxFiles.filter((c) => isReferenceFileName(c.NAME));
      // TODO: Убрать отладочные логи — начало
      dbg(`Совпадений по имени (ключевые слова: ${FILENAME_KEYWORDS.join('/')}): ${nameMatches.length}`);
      nameMatches.forEach((c) => dbg(`  — ${c.NAME} ✓`));
      // TODO: Убрать отладочные логи — конец

      const nameCandidates = [...nameMatches].reverse(); // try newest first

      for (const file of nameCandidates) {
        // TODO: Убрать отладочные логи — начало
        dbg(`Скачиваю файл по имени: "${file.NAME}" (id=${file.ID})...`);
        // TODO: Убрать отладочные логи — конец
        try {
          // Используем fetchDiskFileById: сначала disk.file.get для получения
          // правильного DOWNLOAD_URL с auth-токеном, затем скачивание
          const buffer = await fetchDiskFileById(file.ID);
          // TODO: Убрать отладочные логи — начало
          dbg(`Скачан: ${buffer.byteLength} байт. Пробую распарсить...`);
          // TODO: Убрать отладочные логи — конец
          const result = parseNcReference(buffer);
          // TODO: Убрать отладочные логи — начало
          dbg(`Успех! Справочник загружен: ${result.size} операций`);
          // TODO: Убрать отладочные логи — конец
          return result;
        } catch (e) {
          // TODO: Убрать отладочные логи — начало
          dbg(`Файл "${file.NAME}" не подошёл: ${e instanceof Error ? e.message : String(e)}`);
          // TODO: Убрать отладочные логи — конец
        }
      }

      // 3. No name match or all name-matched files failed — scan all xlsx files
      // TODO: Убрать отладочные логи — начало
      dbg('Файлы по имени не подошли. Сканирую все xlsx по структуре...');
      // TODO: Убрать отладочные логи — конец
      const allCandidates = [...xlsxFiles].reverse();

      for (const file of allCandidates) {
        // Skip files we already tried by name
        if (nameMatches.includes(file)) continue;

        // TODO: Убрать отладочные логи — начало
        dbg(`Скачиваю файл: "${file.NAME}" (id=${file.ID})...`);
        // TODO: Убрать отладочные логи — конец
        try {
          const buffer = await fetchDiskFileById(file.ID);
          // TODO: Убрать отладочные логи — начало
          dbg(`Скачан: ${buffer.byteLength} байт. Пробую распарсить...`);
          // TODO: Убрать отладочные логи — конец
          const result = parseNcReference(buffer);
          // TODO: Убрать отладочные логи — начало
          dbg(`Успех! Справочник найден в "${file.NAME}": ${result.size} операций`);
          // TODO: Убрать отладочные логи — конец
          return result;
        } catch (e) {
          // TODO: Убрать отладочные логи — начало
          dbg(`Файл "${file.NAME}" не подошёл: ${e instanceof Error ? e.message : String(e)}`);
          // TODO: Убрать отладочные логи — конец
        }
      }

      // TODO: Убрать отладочные логи — начало
      dbg('Все xlsx файлы проверены — справочник не найден');
      // TODO: Убрать отладочные логи — конец
      throw new Error(
        'Ни один xlsx-файл в папке справочников не содержит лист «Справочник по Нч»',
      );
    },
    staleTime: 5 * 60 * 1000, // 5 min — reference data rarely changes
    retry: 1,
    enabled: !!REFERENCE_FOLDER_ID,
  });
}

/** Re-export lookupNc for convenience */
export { lookupNc };
